import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import PageSEO from '@/components/PageSEO';
import { Button } from '@/components/ui/button';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { FlaskConical, Search, TrendingUp, Award, Brain, Menu, X, User, ArrowLeftRight } from 'lucide-react';
import { api } from '@/api/api';
import { toast } from 'sonner';
import { Footer } from '@/components/Footer';
import { Input } from '@/components/ui/input';

import { ThemeToggle } from '@/components/ThemeToggle';
import { Checkbox } from '@/components/ui/checkbox';

const PublicExplorer = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isAuthorLoggedIn, setIsAuthorLoggedIn] = useState(false);
  const [compareList, setCompareList] = useState([]);
  const [filters, setFilters] = useState({
    study_type: 'all',
    evidence_level: 'all',
    min_year: 'all',
    limit: 20
  });
  const [sortBy, setSortBy] = useState('newest');

  useEffect(() => {
    loadArticles();
    // Check if author is logged in
    const authorAuth = sessionStorage.getItem('author_authenticated');
    setIsAuthorLoggedIn(!!authorAuth);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadArticles = async () => {
    setLoading(true);
    try {
      const data = await api.exploreArticles(filters);
      let result = data;
      // Filter by search query if provided
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        result = data.filter(article => {
          const matchesTitle = article.title && article.title.toLowerCase().includes(query);
          const matchesJournal = article.journal && article.journal.toLowerCase().includes(query);
          const matchesDOI = article.doi && article.doi.toLowerCase().includes(query);
          const matchesAuthors = article.authors && article.authors.some(author => 
            author.toLowerCase().includes(query)
          );
          return matchesTitle || matchesJournal || matchesDOI || matchesAuthors;
        });
      }
      // Sort
      result.sort((a, b) => {
        switch (sortBy) {
          case 'newest': return (b.publication_year || 0) - (a.publication_year || 0);
          case 'oldest': return (a.publication_year || 0) - (b.publication_year || 0);
          case 'impact_high': return (b.impact_index || 0) - (a.impact_index || 0);
          case 'impact_low': return (a.impact_index || 0) - (b.impact_index || 0);
          case 'title_az': return (a.title || '').localeCompare(b.title || '');
          default: return 0;
        }
      });
      setArticles(result);
    } catch (error) {
      toast.error('Failed to load articles');
    }
    setLoading(false);
  };

  const getEvidenceLevelColor = (level) => {
    switch (level) {
      case 'Level I': return 'bg-green-100 text-green-800';
      case 'Level II': return 'bg-blue-100 text-blue-800';
      case 'Level III': return 'bg-yellow-100 text-yellow-800';
      case 'Level IV': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const toggleCompare = (id) => {
    setCompareList(prev => {
      if (prev.includes(id)) return prev.filter(x => x !== id);
      if (prev.length >= 2) { toast.info('Maximum 2 articles for comparison'); return prev; }
      return [...prev, id];
    });
  };

  return (
    <>
      <PageSEO 
        title="Explore Medical Research Articles | Evidence-Based Analysis | Evidentica"
        description="Browse and filter medical research articles ranked by methodological rigor and impact. Access AI-powered analysis, evidence levels, and comprehensive study evaluations."
        keywords="medical research database, clinical studies, evidence-based medicine, research articles, systematic review, meta-analysis, RCT, case studies"
        canonical={`${process.env.REACT_APP_BACKEND_URL || ''}/explorer`}
      />
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      {/* Navigation */}
      <nav className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center">
            <Link to="/" className="flex items-center space-x-2">
              <FlaskConical className="h-8 w-8 text-blue-600" />
              <span className="text-xl font-bold text-slate-900">Evidentica</span>
            </Link>
            
            {/* Desktop Navigation */}
            <div className="hidden md:flex space-x-4">
              <Link to="/top-impact">
                <Button variant="ghost" data-testid="nav-top-impact-btn">
                  <TrendingUp className="mr-2 h-4 w-4" />
                  Top Impact
                </Button>
              </Link>
              <Link to="/most-rigorous">
                <Button variant="ghost" data-testid="nav-most-rigorous-btn">
                  <Award className="mr-2 h-4 w-4" />
                  Most Rigorous
                </Button>
              </Link>
              <Link to="/compare">
                <Button variant="ghost" data-testid="nav-compare-btn">
                  <ArrowLeftRight className="mr-2 h-4 w-4" />
                  Compare
                </Button>
              </Link>
              {isAuthorLoggedIn ? (
                <Link to="/author/dashboard">
                  <Button variant="ghost" data-testid="nav-author-dashboard-btn">
                    <User className="mr-2 h-4 w-4" />
                    Dashboard
                  </Button>
                </Link>
              ) : (
                <Link to="/author/login">
                  <Button variant="ghost" data-testid="nav-author-login-btn">Login</Button>
                </Link>
              )}
              <ThemeToggle />
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden flex items-center gap-1">
              <ThemeToggle />
              <button
                className="p-2"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? (
                  <X className="h-6 w-6 text-slate-900 dark:text-slate-100" />
              ) : (
                <Menu className="h-6 w-6 text-slate-900 dark:text-slate-100" />
              )}
              </button>
            </div>
          </div>

          {/* Mobile Navigation Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 space-y-2">
              <Link to="/top-impact" onClick={() => setMobileMenuOpen(false)}>
                <Button variant="ghost" className="w-full justify-start" data-testid="nav-top-impact-btn">
                  <TrendingUp className="mr-2 h-4 w-4" />
                  Top Impact
                </Button>
              </Link>
              <Link to="/most-rigorous" onClick={() => setMobileMenuOpen(false)}>
                <Button variant="ghost" className="w-full justify-start" data-testid="nav-most-rigorous-btn">
                  <Award className="mr-2 h-4 w-4" />
                  Most Rigorous
                </Button>
              </Link>
              {isAuthorLoggedIn ? (
                <Link to="/author/dashboard" onClick={() => setMobileMenuOpen(false)}>
                  <Button variant="ghost" className="w-full justify-start" data-testid="nav-author-dashboard-btn">
                    <User className="mr-2 h-4 w-4" />
                    Dashboard
                  </Button>
                </Link>
              ) : (
                <Link to="/author/login" onClick={() => setMobileMenuOpen(false)}>
                  <Button variant="ghost" className="w-full justify-start" data-testid="nav-author-login-btn">Login</Button>
                </Link>
              )}
            </div>
          )}
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-white mb-2" data-testid="explorer-title">Explore Medical Research</h1>
          <p className="text-sm sm:text-base text-slate-600 dark:text-slate-400">Discover evidence-based research ranked by methodological rigor and impact</p>
        </div>

        {/* Disclaimer */}
        <div className="mb-4 sm:mb-6 bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-lg p-3 sm:p-4">
          <p className="text-xs sm:text-sm text-amber-800 dark:text-amber-300">
            <strong>Disclaimer:</strong> The Impact Index is a methodological indicator designed to assess study design strength
            and research visibility. It does not represent clinical truth, guideline recommendations, or treatment standards.
          </p>
        </div>

        {/* Filters */}
        <Card className="mb-8" data-testid="filters-card">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Search className="mr-2 h-5 w-5" />
              Filter Articles
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Search Field */}
              <div>
                <Label>Search Articles</Label>
                <Input
                  placeholder="Search by title, DOI, authors, or journal..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  data-testid="search-input"
                  className="w-full"
                />
              </div>
              
              {/* Other Filters */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 sm:gap-4">
              <div>
                <Label>Study Type</Label>
                <Select value={filters.study_type} onValueChange={(value) => setFilters({ ...filters, study_type: value })}>
                  <SelectTrigger data-testid="filter-study-type">
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="RCT">RCT</SelectItem>
                    <SelectItem value="Meta-analysis">Meta-analysis</SelectItem>
                    <SelectItem value="Systematic review">Systematic Review</SelectItem>
                    <SelectItem value="Prospective cohort">Prospective Cohort</SelectItem>
                    <SelectItem value="Retrospective cohort">Retrospective Cohort</SelectItem>
                    <SelectItem value="Case report">Case Report</SelectItem>
                    <SelectItem value="Case series">Case Series</SelectItem>
                    <SelectItem value="Cross-sectional">Cross-sectional</SelectItem>
                    <SelectItem value="Scoping review">Scoping Review</SelectItem>
                    <SelectItem value="Narrative review">Narrative Review</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Evidence Level</Label>
                <Select value={filters.evidence_level} onValueChange={(value) => setFilters({ ...filters, evidence_level: value })}>
                  <SelectTrigger data-testid="filter-evidence-level">
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All</SelectItem>
                    <SelectItem value="Level I">Level I</SelectItem>
                    <SelectItem value="Level II">Level II</SelectItem>
                    <SelectItem value="Level III">Level III</SelectItem>
                    <SelectItem value="Level IV">Level IV</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Year</Label>
                <Select value={filters.min_year} onValueChange={(value) => setFilters({ ...filters, min_year: value })}>
                  <SelectTrigger data-testid="filter-min-year">
                    <SelectValue placeholder="All time" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All time</SelectItem>
                    <SelectItem value="2025">2025+</SelectItem>
                    <SelectItem value="2024">2024+</SelectItem>
                    <SelectItem value="2020">2020+</SelectItem>
                    <SelectItem value="2015">2015+</SelectItem>
                    <SelectItem value="2010">2010+</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Sort By</Label>
                <Select value={sortBy} onValueChange={(value) => setSortBy(value)}>
                  <SelectTrigger data-testid="filter-sort">
                    <SelectValue placeholder="Newest" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="newest">Newest First</SelectItem>
                    <SelectItem value="oldest">Oldest First</SelectItem>
                    <SelectItem value="impact_high">Highest Impact</SelectItem>
                    <SelectItem value="impact_low">Lowest Impact</SelectItem>
                    <SelectItem value="title_az">Title A-Z</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end gap-2">
                <Button onClick={loadArticles} className="flex-1" data-testid="apply-filters-btn">
                  Apply
                </Button>
                <Button variant="outline" onClick={() => { setFilters({ study_type: 'all', evidence_level: 'all', min_year: 'all' }); setSearchQuery(''); setSortBy('newest'); }} data-testid="clear-filters-btn">
                  Clear
                </Button>
              </div>
            </div>
            </div>
          </CardContent>
        </Card>

        {/* Results Count */}
        {!loading && articles.length > 0 && (
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm text-slate-500" data-testid="results-count">
              Showing <span className="font-semibold text-slate-700">{articles.length}</span> article{articles.length !== 1 ? 's' : ''}
              {searchQuery.trim() && <span> matching &quot;{searchQuery}&quot;</span>}
            </p>
          </div>
        )}

        {/* Articles Grid */}
        <div className="grid grid-cols-1 gap-6" data-testid="articles-grid">
          {loading ? (
            <div className="text-center py-12">
              <p className="text-slate-600">Loading articles...</p>
            </div>
          ) : articles.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-slate-600">No articles found. Try adjusting your filters.</p>
            </div>
          ) : (
            articles.map((article) => (
              <Card key={article.id} className="hover:shadow-lg transition-shadow" data-testid={`article-card-${article.id}`}>
                <CardContent className="pt-4 sm:pt-6">
                  <div className="flex justify-between items-start gap-3 mb-3 sm:mb-4">
                    <div className="flex items-start gap-3 flex-1 min-w-0">
                      <Checkbox
                        checked={compareList.includes(article.id)}
                        onCheckedChange={() => toggleCompare(article.id)}
                        className="mt-1 flex-shrink-0"
                        data-testid={`compare-check-${article.id}`}
                      />
                      <div className="flex-1 min-w-0">
                      <Link to={`/article/${article.id}`}>
                        <h3 className="text-base sm:text-xl font-bold text-slate-900 dark:text-white hover:text-blue-600 mb-2 line-clamp-2" data-testid="article-title">
                          {article.title}
                        </h3>
                      </Link>
                      <div className="flex flex-wrap items-center gap-2 sm:gap-4 text-xs sm:text-sm text-slate-600 dark:text-slate-400 mb-2 sm:mb-3">
                        {article.journal && <span data-testid="article-journal" className="truncate max-w-[180px] sm:max-w-none">{article.journal}</span>}
                        {article.publication_year && <span data-testid="article-year">{article.publication_year}</span>}
                      </div>
                      <div className="flex items-center flex-wrap gap-2">
                        {article.normalized_study_type && (
                          <Badge variant="outline" className="text-xs" data-testid="study-type-badge">{article.normalized_study_type}</Badge>
                        )}
                        {article.evidence_level && (
                          <Badge className={`${getEvidenceLevelColor(article.evidence_level)} text-xs`} data-testid="evidence-level-badge">
                            {article.evidence_level}
                          </Badge>
                        )}
                      </div>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      {article.impact_index === 0 || !article.impact_index ? (
                        <div className="text-center">
                          <div className="text-xl sm:text-2xl font-bold text-amber-500 animate-pulse">
                            ...
                          </div>
                          <div className="text-xs text-amber-600 mt-1">Analyzing</div>
                        </div>
                      ) : (
                        <>
                          <div className="text-3xl sm:text-4xl font-bold text-blue-600" data-testid="impact-index">
                            {article.impact_index.toFixed(1)}
                          </div>
                          <div className="text-xs text-slate-600">Impact</div>
                        </>
                      )}
                    </div>
                  </div>
                  
                  {article.impact_index && (
                    <div className="mt-4">
                      {['Expert opinion', 'Editorial', 'Letter', 'Commentary'].includes(article.normalized_study_type) ? (
                        <div className="flex justify-between text-sm text-slate-600 mb-2">
                          <span className="text-slate-400 italic">Methodology: Not Scorable</span>
                          <span>Visibility only</span>
                        </div>
                      ) : (
                        <div className="flex justify-between text-sm text-slate-600 mb-2">
                          <span>
                            {article.normalized_study_type === 'Case report' || article.normalized_study_type === 'Case series'
                              ? 'Clinical Value (max 35)'
                              : ['Meta-analysis', 'Systematic review', 'Scoping review', 'Narrative review', 'Critical appraisal review'].includes(article.normalized_study_type)
                                ? 'ESS (max 35)'
                                : 'Methodology (max 70)'}
                          </span>
                          <span>Visibility (max 30)</span>
                        </div>
                      )}
                      <Progress 
                        value={article.impact_index} 
                        max={
                          (article.normalized_study_type === 'Case report' || article.normalized_study_type === 'Case series' || ['Meta-analysis', 'Systematic review', 'Scoping review', 'Narrative review', 'Critical appraisal review'].includes(article.normalized_study_type))
                            ? 65 : 100
                        } 
                        className="h-3" 
                        data-testid="impact-progress" 
                      />
                    </div>
                  )}
                  
                  {/* AI Research Analysis Button - Hidden for Case Reports/Series */}
                  {article.normalized_study_type !== 'Case report' && article.normalized_study_type !== 'Case series' && (
                    <div className="mt-4 pt-4 border-t border-slate-200">
                      <Link to={`/research-insight/${article.id}`}>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="w-full text-purple-600 border-purple-300 hover:bg-purple-50"
                          data-testid="view-ai-analysis-btn"
                        >
                          <Brain className="mr-2 h-4 w-4" />
                          View AI Research Analysis
                        </Button>
                      </Link>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>

      <Footer />

      {/* Floating Compare Bar */}
      {compareList.length > 0 && (
        <div className="fixed bottom-0 inset-x-0 bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-700 shadow-lg z-50 p-3" data-testid="compare-bar">
          <div className="max-w-7xl mx-auto flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
              <ArrowLeftRight className="h-4 w-4 text-blue-600" />
              <span><strong>{compareList.length}</strong>/2 selected for comparison</span>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={() => setCompareList([])} data-testid="clear-compare-btn">Clear</Button>
              <Link to={`/compare?a=${compareList[0] || ''}&b=${compareList[1] || ''}`}>
                <Button size="sm" disabled={compareList.length < 2} data-testid="go-compare-btn">
                  <ArrowLeftRight className="mr-2 h-4 w-4" />
                  Compare
                </Button>
              </Link>
            </div>
          </div>
        </div>
      )}
    </div>
    </>
  );
};

export default PublicExplorer;
